import React from 'react';

import { AxisBottom, AxisLeft } from '@visx/axis';
import { GridRows, GridColumns } from '@visx/grid';
import { Group } from '@visx/group';
import { ParentSize } from '@visx/responsive';
import { scaleLinear, scaleBand } from '@visx/scale';
import { BoxPlot } from '@visx/stats';
import {
  withTooltip,
  Tooltip,
  defaultStyles as defaultTooltipStyles,
} from '@visx/tooltip';
import { WithTooltipProvidedProps } from '@visx/tooltip/lib/enhancers/withTooltip';

import colors from '@colors';

interface BoxPlotStats {
  name: string;
  min: number;
  firstQuartile: number;
  median: number;
  thirdQuartile: number;
  max: number;
}

interface ConfidenceBoxPlotStats {
  name: string;
  min: number;
  median: number;
  max: number;
}

interface BoxPlotDataItem {
  boxPlot: BoxPlotStats & ConfidenceBoxPlotStats;
}

type TooltipData =
  | { type: 'box'; data: BoxPlotStats; customTooltip?: React.ReactNode }
  | { type: 'outlier'; value: number };

interface BoxPlotChartBaseProps {
  data: BoxPlotDataItem[];
  width?: number;
  height?: number;
  boxWidth?: number;
  fill?: string;
  stroke?: string;
  outliers?: number[][];
  fillOpacity?: number;
  horizontal?: boolean;
  hideYAxis?: boolean;
  className?: string;
  xAxisLabel?: string;
  medianColor?: string;
  isPercentage?: boolean;
  tooltipOverride?: (data: BoxPlotStats) => React.ReactNode;
  fixedDomain?: { min: number; max: number };
}

type BoxPlotChartProps = BoxPlotChartBaseProps &
  WithTooltipProvidedProps<TooltipData>;

const BoxPlotChart: React.FC<BoxPlotChartProps> = ({
  data,
  width = 600,
  height = 300,
  boxWidth = 24,
  fill,
  stroke = 'transparent',
  outliers = [],
  fillOpacity = 1,
  horizontal = false,
  hideYAxis = false,
  xAxisLabel,
  medianColor = colors.neutral['800'],
  tooltipData,
  tooltipOpen,
  tooltipTop,
  tooltipLeft,
  showTooltip,
  hideTooltip,
  className,
  isPercentage,
  tooltipOverride,
  fixedDomain,
}) => {
  const allValues = data.flatMap((d, i) => [
    d.boxPlot.min,
    d.boxPlot.median,
    d.boxPlot.max,
    ...(outliers[i] ?? []),
  ]);

  const margin = { top: 20, right: 40, bottom: horizontal ? 50 : 44, left: 40 };
  const actualMinValue = Math.min(...allValues);
  const actualMaxValue = Math.max(...allValues);

  const padding = 5;

  const maxAbsValue =
    Math.max(Math.abs(actualMinValue), Math.abs(actualMaxValue)) + padding;
  const minValue = -maxAbsValue;
  const maxValue = maxAbsValue;

  // Define separate value scales
  const verticalValueScale = scaleLinear({
    domain: [minValue, maxValue],
    range: [height - margin.bottom, margin.top], // Y-axis: Lower values bottom, higher values top
    nice: true,
  });

  const horizontalValueScale = scaleLinear({
    domain: fixedDomain
      ? [fixedDomain.min, fixedDomain.max]
      : [minValue, maxValue],
    range: [margin.left, width - margin.right], // X-axis: Negative left, positive right
    nice: fixedDomain ? false : true,
  });

  const valueScale = horizontal ? horizontalValueScale : verticalValueScale;

  const positionScale = scaleBand({
    domain: data.map((_, i) => i),
    range: horizontal
      ? [margin.top, height - margin.bottom] // Horizontal: Y-axis spread
      : [margin.left, width - margin.right], // Vertical: X-axis spread
    padding: 1,
  });

  const constrainedWidth = Math.min(100, boxWidth);

  const formatValue = (v: number) =>
    isPercentage ? `${v.toLocaleString()}%` : v.toLocaleString();

  const shouldAdjustLabelsAndValues = (
    min: number,
    med: number,
    max: number,
  ): { shouldRotateLabels: boolean; shouldStaggerValues: boolean } => {
    let shouldRotateLabels = false;
    let shouldStaggerValues = false;

    const pxMin = valueScale(min);
    const pxMed = valueScale(med);
    const pxMax = valueScale(max);

    if (width < 480) {
      shouldRotateLabels = true;
      shouldStaggerValues = true;
    }

    if (Math.abs(pxMin - pxMed) < 60 || Math.abs(pxMed - pxMax) < 60) {
      shouldRotateLabels = true;
    }

    if (Math.abs(pxMin - pxMed) < 30 || Math.abs(pxMed - pxMax) < 30) {
      shouldStaggerValues = true;
    }

    return { shouldRotateLabels, shouldStaggerValues };
  };

  return (
    <div className={`${className} relative`}>
      <svg width={width} height={height}>
        <image
          href="/assets/images/searchpilot-chart-background.png"
          x={width / 3}
          y={120}
          width={width / 3}
          height={height / 3}
          preserveAspectRatio="xMidYMid slice"
        />
        {!horizontal ? (
          <GridRows
            scale={valueScale} // Use valueScale for vertical grid lines
            width={width - margin.left - margin.right}
            stroke={colors.neutral['100']}
            strokeOpacity={0.7}
            strokeWidth={2}
            left={margin.left}
          />
        ) : (
          <Group>
            <GridColumns
              scale={valueScale} // Use valueScale for horizontal box plot grid
              height={height - margin.top - margin.bottom}
              stroke={colors.neutral['100']}
              strokeOpacity={0.7}
              strokeWidth={2}
              top={margin.top}
            />
            {horizontal && (
              <line
                x1={valueScale(0)}
                x2={valueScale(0)}
                y1={margin.top}
                y2={height - margin.bottom}
                stroke={colors.neutral['300']}
                strokeWidth={2}
              />
            )}
          </Group>
        )}

        {/* Render BoxPlot component for each data point */}
        {data.map((d, i) => {
          // Calculate position for each BoxPlot
          const position = positionScale(i); // Based on the index

          if (position === undefined) return null;

          const { shouldRotateLabels, shouldStaggerValues } =
            shouldAdjustLabelsAndValues(
              d.boxPlot.min,
              d.boxPlot.median,
              d.boxPlot.max,
            );

          return (
            <React.Fragment key={`boxplot-${i}`}>
              {horizontal && (
                <Group key={`labels-${i}`}>
                  {/* Min Label */}
                  <text
                    x={valueScale(d.boxPlot.min)}
                    y={position - (shouldStaggerValues ? 22 : 8)}
                    fontSize={12}
                    fontWeight="bold"
                    textAnchor="middle"
                    fill="black"
                  >
                    {formatValue(d.boxPlot.min)}
                  </text>
                  <text
                    x={valueScale(d.boxPlot.min)}
                    y={position + boxWidth + (shouldRotateLabels ? 22 : 12)}
                    fontSize={10}
                    textAnchor="middle"
                    fill="#666"
                    transform={
                      shouldRotateLabels
                        ? `rotate(-30, ${valueScale(d.boxPlot.min)}, ${position + boxWidth + 42})`
                        : undefined
                    }
                  >
                    Lower Bound
                  </text>

                  {/* Median Label */}
                  <text
                    x={valueScale(d.boxPlot.median)}
                    y={position - 8}
                    fontSize={12}
                    fontWeight="bold"
                    textAnchor="middle"
                  >
                    {formatValue(d.boxPlot.median)}
                  </text>
                  <text
                    x={valueScale(d.boxPlot.median)}
                    y={position + boxWidth + (shouldRotateLabels ? 22 : 12)}
                    fontSize={10}
                    textAnchor="middle"
                    fill="#666"
                    transform={
                      shouldRotateLabels
                        ? `rotate(-30, ${valueScale(d.boxPlot.median)}, ${position + boxWidth + 42})`
                        : undefined
                    }
                  >
                    Best Estimate
                  </text>

                  {/* Max Label */}
                  <text
                    x={valueScale(d.boxPlot.max)}
                    y={position - (shouldStaggerValues ? 22 : 8)}
                    fontSize={12}
                    fontWeight="bold"
                    textAnchor="middle"
                    fill="black"
                  >
                    {formatValue(d.boxPlot.max)}
                  </text>
                  <text
                    x={valueScale(d.boxPlot.max)}
                    y={position + boxWidth + (shouldRotateLabels ? 22 : 12)}
                    fontSize={10}
                    textAnchor="middle"
                    fill="#666"
                    transform={
                      shouldRotateLabels
                        ? `rotate(-30, ${valueScale(d.boxPlot.max)}, ${position + boxWidth + 42})`
                        : undefined
                    }
                  >
                    Upper Bound
                  </text>
                </Group>
              )}
              <BoxPlot
                key={i}
                valueScale={valueScale}
                boxWidth={boxWidth}
                boxProps={{
                  fill: fill,
                  stroke: 'transparent',
                  fillOpacity: fillOpacity,
                  rx: 8,
                  ry: 8,
                  onMouseOver: () => {
                    showTooltip({
                      tooltipTop: horizontal
                        ? position + boxWidth / 2
                        : valueScale(d.boxPlot.median),
                      tooltipLeft: horizontal
                        ? valueScale(d.boxPlot.median) + 10
                        : position + constrainedWidth + 5,
                      tooltipData: {
                        type: 'box',
                        data: d.boxPlot,
                        customTooltip: tooltipOverride
                          ? tooltipOverride(d.boxPlot)
                          : undefined,
                      },
                    });
                  },
                  onMouseLeave: hideTooltip,
                }}
                firstQuartile={d.boxPlot.min}
                median={d.boxPlot.median}
                medianProps={{
                  stroke: medianColor,
                  strokeWidth: 3,
                  strokeOpacity: 1,
                }}
                thirdQuartile={d.boxPlot.max}
                outliers={outliers[i] ?? []}
                outlierProps={{
                  fill: 'red',
                  stroke: 'black',
                  strokeWidth: 1,
                  onMouseOver: () => {
                    showTooltip({
                      tooltipTop: valueScale(d.boxPlot.min),
                      tooltipLeft: tooltipLeft || 0,
                      tooltipData: {
                        type: 'outlier',
                        value: d.boxPlot.max,
                      },
                    });
                  },
                  onMouseLeave: hideTooltip,
                }}
                stroke={stroke}
                strokeWidth={1}
                left={horizontal ? valueScale(d.boxPlot.min) : position} // Correct horizontal placement
                top={horizontal ? position : valueScale(d.boxPlot.max)} // Correct vertical placement
                container={false}
                horizontal={horizontal}
                minProps={{ style: { display: 'none' } }}
                maxProps={{ style: { display: 'none' } }}
              />
            </React.Fragment>
          );
        })}
        {/* Axes */}
        {horizontal ? (
          <Group>
            <rect
              x={margin.left - 40}
              y={height - margin.bottom} // Position at bottom
              width={width - margin.left - margin.right + 80}
              height={margin.bottom}
              fill={'transparent'}
            />
            <AxisBottom
              scale={valueScale}
              top={height - margin.bottom}
              hideTicks={false}
              tickStroke={colors.neutral['100']}
              strokeWidth={2}
              stroke={colors.neutral['100']}
              label={xAxisLabel}
              labelProps={{
                fontSize: 12,
                fontFamily: 'Lucida Grande',
                fill: colors.neutral.DEFAULT,
                fontWeight: 'bold',
              }}
              labelOffset={15}
              tickFormat={(v) => formatValue(v as number)}
            />
            {!hideYAxis && (
              <AxisLeft
                scale={positionScale}
                left={margin.left}
                strokeWidth={2}
                stroke={colors.neutral['100']}
              />
            )}
          </Group>
        ) : (
          <>
            <AxisBottom
              strokeWidth={2}
              stroke={colors.neutral['100']}
              scale={positionScale}
              top={height - margin.bottom}
            />
            {!hideYAxis && (
              <AxisLeft
                scale={valueScale}
                left={margin.left}
                tickFormat={(v) => formatValue(v as number)}
              />
            )}
          </>
        )}
      </svg>
      {/* Tooltip */}
      {tooltipOpen && tooltipData && (
        <Tooltip
          top={tooltipTop}
          left={tooltipLeft}
          style={defaultTooltipStyles}
        >
          {tooltipData.type === 'box' ? (
            tooltipData.customTooltip ? (
              tooltipData.customTooltip
            ) : (
              <div className="flex flex-col gap-1 text-xs font-normal leading-[18px] tracking-[-0.3px]">
                <strong>{tooltipData.data.name}</strong>
                <p>Minimum: {formatValue(tooltipData.data.min)}</p>
                <p>Median: {formatValue(tooltipData.data.median)}</p>
                <p>Maximum: {formatValue(tooltipData.data.max)}</p>
              </div>
            )
          ) : (
            <div className="text-xs">
              <strong>Outlier:</strong>
              <p>Value: {formatValue(tooltipData.value)}</p>
            </div>
          )}
        </Tooltip>
      )}
    </div>
  );
};

const BoxPlotWithTooltip = withTooltip<BoxPlotChartProps, TooltipData>(
  BoxPlotChart,
);

export default function ResponsiveBoxPlotChart(props) {
  const { data, ...rest } = props;
  return (
    <ParentSize debounceTime={150}>
      {({ width = 600, height = 400 }) => {
        // Optional: set a default height if height is 0
        const chartHeight = height === 0 ? 300 : height;
        const chartWidth = width === 0 ? 600 : width;
        return (
          <BoxPlotWithTooltip
            data={data}
            width={chartWidth}
            height={chartHeight}
            {...rest}
          />
        );
      }}
    </ParentSize>
  );
}

BoxPlotChart.defaultProps = {
  fill: colors.neutral['300'],
  className: '',
};
